import { SlicePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { ResultState } from '@cds/betting-offer/domain-specific/bet-builder';
import {
    BetBuilderButtonSource,
    BetBuilderMarket,
    BetBuilderPrecreatedModel,
    EventModel,
    EventOption,
    EventOptionGroup,
} from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';

import { BetBuilderTrackingValues } from '../bet-builder/bet-builder-tracking';
import { BetBuilderButtonComponent } from '../bet-builder/components/button/bet-builder-button.component';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupRendererDirective } from './option-group-renderer.directive';
import { OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { PrecreatedBabTrackingService } from './precreated-bab-tracking.service';
import { BetBuilderOptionGroup, Option } from './precreated-bet-builder.model';
import { isOptionVisible } from './precreated-group-option-utils';
import { PrecreatedGroupOptionComponent } from './precreated-group-option.component';
import { PrecreatedOptionComponent } from './precreated-option.component';

@Component({
    selector: 'ms-precreated-bab-option',
    templateUrl: './precreated-bab-option.component.html',
    imports: [
        SlicePipe,
        MarketHelpComponent,
        OptionGroupRendererDirective,
        PrecreatedOptionComponent,
        PrecreatedGroupOptionComponent,
        BetBuilderButtonComponent,
        OptionPanelBottomActionComponent,
    ],
})
export class PrecreatedBabOptionComponent implements OnInit, OnChanges {
    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));

    @Input() showMarketHelp = false;
    @Input() event: EventModel;
    @Input() optionGroup: EventOptionGroup;
    @Input() options: EventOption[];
    @Input() fixtureId: string;
    @Input() tabChanged = false;
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() buttonSource?: BetBuilderButtonSource;
    @Input() tracking?: string;
    @Input() trackingModuleType = '';
    @Input() betBuilderEpcotEnabled = false;
    @Input() hideBetBuilderButton = false;
    @Input() showAll = false;
    @Input() sourceOfPick: SourceOfPick;

    @Output() showAllRequested = new EventEmitter<void>();

    betbuilderModel: BetBuilderPrecreatedModel | undefined;
    marketsList: BetBuilderMarket[];
    optionGroups: BetBuilderOptionGroup[] = [];
    betbuilderTrackingValues = BetBuilderTrackingValues;
    eventOptionGroups: EventOptionGroup[] = [];
    visibleOptionsLimit: number;
    eventHelpText: string;

    showMoreData: ShowMoreData;
    readonly showSGPButtonsInPrebuiltSGP: boolean;

    constructor(
        public sitecore: Sitecore,
        public betBuilderConfig: BetBuilderConfig,
        public marketGroupingConfig: MarketGroupingConfig,
        private trackingService: PrecreatedBabTrackingService,
        private optionGroupService: OptionGroupService,
    ) {
        this.showSGPButtonsInPrebuiltSGP = betBuilderConfig.showSGPButtonsInPrebuiltSGP ?? true;
    }

    ngOnInit(): void {
        this.betbuilderModel = this.optionGroupService.mapEventOptionGroup(this.event.id, this.optionGroup);
        this.marketsList = this.event.precreatedOptionGroups?.length ? [] : (this.betbuilderModel?.themedTabMarkets ?? []);

        if (this.optionGroup.options?.length) {
            this.fixtureId = this.event.betBuilderTradingV2FixtureId || this.event.id;
        }

        if (this.sitecore.betBuilder.messages) {
            this.eventHelpText = this.sitecore.betBuilder.messages.PrecreatedBuildABetTitleHelpText;
        }

        this.eventOptionGroups.push(this.optionGroup);
    }

    ngOnChanges(): void {
        this.betbuilderModel = this.optionGroupService.mapEventOptionGroup(this.event.id, this.optionGroup);
        this.marketsList = this.betbuilderModel?.themedTabMarkets ?? [];
        if (this.tabChanged) {
            this.visibleOptionsLimit = this.optionsLimitOverride ?? this.betBuilderConfig.initialPrecreatedMarketsPerSport;
        } else {
            this.visibleOptionsLimit =
                this.optionsLimitOverride ?? this.visibleOptionsLimit ?? this.betBuilderConfig.initialPrecreatedMarketsPerSport;
        }

        if (this.event.precreatedOptionGroups?.length && this.event.precreatedOptionGroups?.length > 0) {
            this.marketsList = [];
            this.mapAngstromOptionGroups();
        }

        const itemsCollection = this.optionGroups?.length ? this.optionGroups : this.marketsList;
        this.showMoreData = {
            totalCount: itemsCollection.length,
            initialVisibleCount: this.visibleOptionsLimit,
            pageSize: this.optionsLimitOverride ? itemsCollection.length : this.betBuilderConfig.incrementForPrecreatedMarketsShowMore,
        };

        if (this.isInSheetView) {
            this.visibleOptionsLimit = itemsCollection.length;
        }
    }

    trackOption(index: number, option: BetBuilderMarket): number {
        return option.id;
    }

    trackGroup(index: number, group: BetBuilderOptionGroup): string {
        return group.externalGroupInfo.groupId;
    }

    onShowMore({ state, visibleCount }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        if (state !== 'collapsed') {
            this.visibleOptionsLimit = visibleCount;
            this.trackingService.trackShowMoreClick(true);
        } else {
            this.visibleOptionsLimit = this.showMoreData.initialVisibleCount;
            this.trackingService.trackShowMoreClick(false);
        }
    }

    private mapAngstromOptionGroups() {
        this.optionGroups = [];

        let validGroups =
            this.event.precreatedOptionGroups?.filter((g) => {
                if (this.event.betBuilderInfo?.betBuilderProvider !== BetBuilderProvider.BybFootball) {
                    return (
                        g.builderOptionPricing.legInformation.length &&
                        g.builderOptionPricing.legInformation.every((l) => l.resultState === ResultState.Open) &&
                        g.builderOptionPricing.odds
                    );
                }

                return !!g.builderOptionPricing.odds;
            }) ?? [];
        const onlyOneGroup = validGroups?.find((g) => g.groupId === this.optionGroup.id);
        if (onlyOneGroup) {
            validGroups = [onlyOneGroup];
        }

        validGroups?.forEach((group) => {
            const optionNames: Option[] = group.options
                .map((optionInGroup) => {
                    const option = this.optionGroup.options.find((o) => o.id === optionInGroup.id);
                    if (option && isOptionVisible(option)) {
                        return {
                            name: option?.name || '',
                            marketName: this.event.marketsDictionary[optionInGroup.marketId][0].name || '',
                        };
                    }

                    return null;
                })
                .filter(hasValue);

            if (optionNames.length === group.options.length) {
                const groupViewModel: BetBuilderOptionGroup = {
                    options: optionNames,
                    externalGroupInfo: group,
                };
                this.optionGroups.push(groupViewModel);
            }
        });
    }
}
